import { Injectable, inject } from '@angular/core';
import { LoadingController } from '@ionic/angular';
import { Console } from '@shared/providers/custom-logger';

/**
 * Servizio per la gestione automatica del loading.
 * Mostra un loading solo se la richiesta supera un determinato tempo (default: 500ms).
 */
@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  private loadingController = inject(LoadingController);
  private loadingElement: HTMLIonLoadingElement | null = null;
  private loadingTimer: any = null;
  private readonly LOADING_DELAY = 500; // 500ms

  /**
   * Avvia il timer per il loading.
   * Se la richiesta non viene completata entro LOADING_DELAY, mostra il loading.
   */
  startLoading(): void {
    if (!this.loadingTimer) {
      this.loadingTimer = setTimeout(async () => {
        await this.showLoading();
      }, this.LOADING_DELAY);
    }
  }

  /**
   * Mostra subito il loading (senza attesa).
   * Utile quando serve feedback immediato oltre al timer dell'interceptor HTTP.
   */
  startLoadingImmediate(): void {
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }
    void this.showLoading();
  }

  /**
   * Nasconde il loading e cancella il timer.
   */
  stopLoading(): void {
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }
    this.hideLoading();
  }

  /**
   * Mostra il loading se non è già visibile.
   */
  private async showLoading(): Promise<void> {
    if (!this.loadingElement) {
      try {
        this.loadingElement = await this.loadingController.create({
          message: 'Caricamento dati in corso...',
          spinner: 'circular',
          cssClass: 'custom-loading',
        });
        await this.loadingElement.present();
      } catch (error) {
        Console.log('Errore nella creazione del loading:', error, 'warn');
      }
    }
  }

  /**
   * Nasconde il loading se è visibile.
   */
  private async hideLoading(): Promise<void> {
    if (this.loadingElement) {
      try {
        await this.loadingElement.dismiss();
      } catch (error) {
        // Ignora errori di dismiss (es. se già chiuso)
      }
      this.loadingElement = null;
    }
  }
}
